"use client";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import FormTextField from "@/src/components/common/broadsheet/form-text-field";
import SchemaWrapper from "@/src/components/common/form/SchemaWrapper";
import SocialIcons from "@/src/components/common/pages/social-icons/social-icons";
import {
  captchaButtonSx,
  captchaFieldSx,
  captchaFrameSx,
  captchaImageStyle,
  fieldSx,
  FormMasthead,
  FormSection,
  requiredLabel,
  StandingNote,
} from "@/src/components/common/broadsheet/form-kit";
import { getBaseUrlForCSR } from "@/src/utils/fetch-helpers";
import VISUALLY_HIDDEN from "@/src/utils/visually-hidden";
import {
  actionSx,
  doubleRule,
  FONT_UI,
  SHEET_GUTTER,
} from "@/src/components/common/broadsheet/tokens";

const REQUEST_TYPES = [
  { value: "0", label: "Reviewer" },
  { value: "1", label: "Associate Editor" },
  { value: "2", label: "Both" },
];

const API_BASE = "/api/review-registration";
const CAPTCHA_API = "/api/captcha";
const SEARCH_DEBOUNCE_MS = 300;
const KEYWORD_MIN_QUERY_LENGTH = 2;

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const PHOTO_EXTENSIONS = ["jpg", "jpeg", "png"];
const RESUME_EXTENSIONS = ["pdf", "docx", "zip"];
const AFFILIATION_PART_MAX_LENGTH = 250;
const PHONE_MIN_DIGITS = 7;
const PHONE_MAX_DIGITS = 14;
const IRAN_ISO = "IR";
const defaultFormValues = {
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  country_id: "",
  address: "",
  affiliation: {
    department: "",
    institution: "",
    street_address: "",
    city: "",
    state_or_province: "",
    postal_code: "",
    country_id: "",
  },
  request_type: "",
  journal_id: "",
  classifications: [] as ReviewerRegistrationClassification[],
  keywords: [] as string[],
  captcha_phrase: "",
};

type FormValues = typeof defaultFormValues;

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const validateFile = (
  file: File | null,
  extensions: string[],
  label: string,
) => {
  if (!file) {
    return `${label} is required`;
  }

  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";

  if (!extensions.includes(extension)) {
    return `${label} must be a ${extensions.join(", ")} file`;
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `${label} must be smaller than ${MAX_FILE_SIZE_MB} MB`;
  }

  return null;
};

const mapClassifications = (
  payload: any,
): ReviewerRegistrationClassification[] =>
  Array.isArray(payload?.data) ? payload.data : [];

const mapKeywords = (payload: any): string[] =>
  Array.isArray(payload?.data) ? payload.data : [];

function useRemoteOptions<T>(
  path: string,
  mapResponse: (payload: any) => T[],
  minQueryLength = 0,
) {
  const [options, setOptions] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useState("");
  const latestRequest = useRef(0);
  const isQueryUsable = query.trim().length >= minQueryLength;

  useEffect(() => {
    if (!isQueryUsable) {
      return;
    }

    const requestId = ++latestRequest.current;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setIsLoading(true);

      try {
        const response = await fetch(
          `${getBaseUrlForCSR()}${path}?q=${encodeURIComponent(query.trim())}`,
          { signal: controller.signal },
        );
        const payload = response.ok ? await response.json() : null;

        if (requestId === latestRequest.current) {
          setOptions(payload ? mapResponse(payload) : []);
        }
      } catch {
        if (requestId === latestRequest.current) {
          setOptions([]);
        }
      } finally {
        if (requestId === latestRequest.current) {
          setIsLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [path, mapResponse, isQueryUsable, query]);

  return {
    options: isQueryUsable ? options : [],
    isLoading: isQueryUsable && isLoading,
    setQuery,
  };
}

const FileField = ({
  label,
  hint,
  accept,
  file,
  error,
  inputRef,
  onSelect,
  onClear,
}: {
  label: string;
  hint: string;
  accept: string;
  file: File | null;
  error?: string | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onSelect: (event: ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
}) => (
  <Box
    sx={{
      height: "100%",
      p: 1,
      border: "1px dashed",
      borderColor: error ? "error.main" : "custom.ruleHairline",
      borderRadius: 0,
      backgroundColor: "custom.newsprintRaised",
    }}
  >
    <Stack spacing={1}>
      <Typography
        component="p"
        sx={{
          m: 0,
          fontFamily: FONT_UI,
          fontSize: "0.8125rem",
          fontWeight: 600,
          color: "custom.ink",
        }}
      >
        {requiredLabel(label, true)}
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ alignItems: { xs: "stretch", sm: "center" } }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={onSelect}
          aria-label={label}
          style={VISUALLY_HIDDEN}
        />
        <Button
          type="button"
          onClick={() => inputRef.current?.click()}
          sx={{ ...actionSx("outline"), flexShrink: 0 }}
        >
          {file ? "Replace file" : "Choose file"}
        </Button>
        {file && (
          <Button
            variant="text"
            color="error"
            type="button"
            onClick={onClear}
            sx={{
              ...actionSx("outline", "small"),
              borderColor: "error.main",
              color: "error.main",
              "&:hover": {
                borderColor: "error.main",
                color: "error.main",
                backgroundColor: "custom.newsprint",
              },
            }}
          >
            Remove
          </Button>
        )}
      </Stack>
      {file && (
        <Chip
          label={`${file.name} (${formatFileSize(file.size)})`}
          size="small"
          variant="outlined"
          sx={{ maxWidth: "100%", alignSelf: "flex-start", borderRadius: 0 }}
        />
      )}
      <FormHelperText error={!!error} sx={{ m: 0 }}>
        {error ?? hint}
      </FormHelperText>
    </Stack>
  </Box>
);

const getApiMessage = (payload: any) =>
  payload?.data?.message ||
  payload?.message ||
  "Unable to submit your registration request.";

const applyServerErrors = (
  payload: any,
  setError: (name: string, message: string) => void,
) => {
  const errors = payload?.data?.errors;

  if (!errors || typeof errors !== "object") {
    return false;
  }

  const fieldNames = Object.keys(defaultFormValues);
  let hasFieldError = false;

  Object.entries(errors).forEach(([field, messages]) => {
    const message = Array.isArray(messages) ? messages[0] : messages;

    if (typeof message !== "string") {
      return;
    }

    const owner = fieldNames.find(
      (name) => name === field || field.startsWith(`${name}.`),
    );

    if (owner) {
      hasFieldError = true;
      setError(field, message);
    }
  });

  return hasFieldError;
};

export default function ReviewerRegistrationForm({
  journals,
  lockedJournalId,
  introduction,
}: {
  journals: ReviewerRegistrationJournal[];
  lockedJournalId: number | null;
  introduction: string;
}) {
  const formRef = useRef<SchemaWrapperRef>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const resumeInputRef = useRef<HTMLInputElement>(null);
  // Read inside the yup test, which only receives the phone value itself.
  const selectedCountryRef = useRef<ReviewerRegistrationCountry | null>(null);

  const [countries, setCountries] = useState<ReviewerRegistrationCountry[]>([]);
  const [captcha, setCaptcha] = useState<Captcha>({ captcha: "", key: "" });
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const [selectedJournalId, setSelectedJournalId] = useState<number | null>(
    lockedJournalId,
  );
  const [photo, setPhoto] = useState<File | null>(null);
  const [resume, setResume] = useState<File | null>(null);
  const [fileErrors, setFileErrors] = useState<{
    photo?: string | null;
    resume?: string | null;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<FormMessage | null>(null);

  const classificationSearch = useRemoteOptions(
    `${API_BASE}/classifications`,
    mapClassifications,
  );
  const keywordSearch = useRemoteOptions(
    `${API_BASE}/keywords`,
    mapKeywords,
    KEYWORD_MIN_QUERY_LENGTH,
  );

  const lockedJournal = useMemo(
    () => journals.find((journal) => journal.id === lockedJournalId) ?? null,
    [journals, lockedJournalId],
  );

  const initialValues = useMemo(
    () => ({
      ...defaultFormValues,
      journal_id: lockedJournal ? String(lockedJournal.id) : "",
    }),
    [lockedJournal],
  );

  const schemaConfig = useMemo(
    () =>
      ({
        first_name: { type: "string", label: "First name", required: true },
        last_name: { type: "string", label: "Last name", required: true },
        email: { type: "string", label: "Email", required: true, email: true },
        phone_number: {
          type: "string",
          label: "Phone number",
          required: true,
          test: (value: any) => {
            const phone = String(value ?? "").trim();

            if (!phone) {
              return true;
            }

            if (!/^\d+$/.test(phone)) {
              return "Phone number must contain digits only, with no spaces or symbols";
            }

            if (selectedCountryRef.current?.iso === IRAN_ISO) {
              return (
                /^09\d{9}$/.test(phone) ||
                "Iranian mobile numbers must be 11 digits starting with 09"
              );
            }

            return (
              (phone.length >= PHONE_MIN_DIGITS &&
                phone.length <= PHONE_MAX_DIGITS) ||
              `Phone number must be between ${PHONE_MIN_DIGITS} and ${PHONE_MAX_DIGITS} digits`
            );
          },
        },
        country_id: { type: "string", label: "Country", required: true },
        address: { type: "string", label: "Address", required: true },
        affiliation: {
          type: "object",
          label: "Affiliation",
          fields: {
            institution: { type: "string", label: "Institution", required: true },
            city: { type: "string", label: "City", required: true },
            country_id: {
              type: "string",
              label: "Affiliation country",
              required: true,
              requiredMessage: "Affiliation country is required",
            },
          },
        },
        request_type: { type: "string", label: "Request type", required: true },
        journal_id: {
          type: "string",
          label: "Journal",
          required: !lockedJournal,
        },
        classifications: {
          type: "array",
          label: "Classifications",
          required: true,
          requiredMessage: "Select at least one classification",
        },
        keywords: {
          type: "array",
          label: "Keywords",
          required: true,
          requiredMessage: "Add at least one keyword",
        },
        captcha_phrase: {
          type: "string",
          label: "Security code",
          required: true,
        },
      }) as SchemaConfig,
    [lockedJournal],
  );

  useEffect(() => {
    const controller = new AbortController();

    const loadCountries = async () => {
      try {
        const response = await fetch(
          `${getBaseUrlForCSR()}${API_BASE}/countries`,
          { signal: controller.signal },
        );

        if (response.ok) {
          const payload = await response.json();
          setCountries(Array.isArray(payload?.data) ? payload.data : []);
        }
      } catch {
        // A failed lookup leaves the country field empty; reloading retries.
      }
    };

    loadCountries();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadCaptcha = async () => {
      setCaptcha({ captcha: "", key: "" });

      try {
        const journalParam = selectedJournalId
          ? `?journalId=${selectedJournalId}`
          : "";
        const response = await fetch(
          `${getBaseUrlForCSR()}${CAPTCHA_API}${journalParam}`,
          { signal: controller.signal, cache: "no-store" },
        );

        if (!response.ok) {
          return;
        }

        const payload = await response.json();

        if (payload?.data?.key) {
          setCaptcha(payload.data);
        }
      } catch {
        // Leaves submit disabled; the refresh button retries.
      }
    };

    loadCaptcha();

    return () => controller.abort();
  }, [selectedJournalId, captchaNonce]);

  const refreshCaptcha = () => setCaptchaNonce((nonce) => nonce + 1);

  const handleFileSelect =
    (
      setFile: (file: File | null) => void,
      extensions: string[],
      label: string,
      key: "photo" | "resume",
    ) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null;

      setFile(file);
      setFileErrors((errors) => ({
        ...errors,
        [key]: file ? validateFile(file, extensions, label) : null,
      }));
    };

  const clearFile = (
    setFile: (file: File | null) => void,
    inputRef: React.RefObject<HTMLInputElement | null>,
    key: "photo" | "resume",
  ) => {
    setFile(null);
    setFileErrors((errors) => ({ ...errors, [key]: null }));

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const validateFiles = () => {
    const photoError = validateFile(photo, PHOTO_EXTENSIONS, "Profile image");
    const resumeError = validateFile(resume, RESUME_EXTENSIONS, "CV");

    setFileErrors({ photo: photoError, resume: resumeError });

    return !photoError && !resumeError;
  };

  const handleRegistration = async (values: FormValues) => {
    if (isSubmitting) {
      return;
    }

    if (!validateFiles()) {
      setMessage({
        severity: "error",
        message: "Please attach both required documents.",
      });

      return;
    }

    const journalId = lockedJournal
      ? String(lockedJournal.id)
      : values.journal_id;
    const journal = journals.find(
      (item) => String(item.id) === String(journalId),
    );

    if (!journal) {
      setMessage({
        severity: "error",
        message: "Please select a journal before submitting.",
      });

      return;
    }

    const formData = new FormData();
    formData.append("first_name", values.first_name.trim());
    formData.append("last_name", values.last_name.trim());
    formData.append("email", values.email.trim());
    formData.append("phone_number", values.phone_number.trim());
    formData.append("country_id", values.country_id);
    formData.append("address", values.address.trim());
    Object.entries(values.affiliation).forEach(([part, value]) =>
      formData.append(`affiliation[${part}]`, String(value ?? "").trim()),
    );
    formData.append("request_type", values.request_type);
    formData.append("journal_id", journalId);
    formData.append("captcha_phrase", values.captcha_phrase.trim());
    formData.append("captcha_key", captcha.key);
    formData.append("photo", photo as File);
    formData.append("resume", resume as File);
    values.classifications.forEach((classification) =>
      formData.append("classifications[]", String(classification.id)),
    );
    values.keywords.forEach((keyword) =>
      formData.append("keywords[]", keyword),
    );

    setIsSubmitting(true);
    setMessage(null);

    try {
      const response = await fetch(
        `${journal.apiBase}/v1/review/request/register`,
        { method: "POST", body: formData },
      );
      const body = await response.json().catch(() => null);

      if (response.ok) {
        setMessage({
          severity: "success",
          message:
            body?.data?.message ??
            "Your request has been submitted for review by the Editor-in-Chief.",
        });
        formRef.current?.reset(initialValues);
        clearFile(setPhoto, photoInputRef, "photo");
        clearFile(setResume, resumeInputRef, "resume");
      } else {
        const hasFieldErrors = applyServerErrors(body, (name, fieldMessage) =>
          formRef.current?.setError(name, fieldMessage),
        );

        setMessage({
          severity: "error",
          message: hasFieldErrors
            ? "Please correct the highlighted fields and try again."
            : getApiMessage(body),
        });
      }
    } catch (error) {
      console.error("Error submitting reviewer registration:", error);
      setMessage({
        severity: "error",
        message: "Unable to submit your registration request.",
      });
    } finally {
      setIsSubmitting(false);
      refreshCaptcha();
    }
  };

  return (
    <Box sx={{ px: SHEET_GUTTER, pb: { xs: 1.5, md: 2.5 } }}>
      <Box
        sx={{ pt: { xs: 1, md: 1.5 }, pb: { xs: 0.75, md: 1 }, ...doubleRule }}
      >
        <FormMasthead title="Reviewer and AE Registration">
          <SocialIcons direction="column" />
        </FormMasthead>
      </Box>

      <SchemaWrapper
        ref={formRef}
        config={schemaConfig}
        defaultValues={initialValues}
        onSubmit={(values) => handleRegistration(values as FormValues)}
      >
        <Stack spacing={2}>
          <StandingNote>{introduction}</StandingNote>

          <FormSection
            step={1}
            title="Personal information"
            description="Tell us who you are and how the editorial office can reach you."
          >
            <Grid container spacing={{ xs: 0, md: 2 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormTextField
                  isRequired
                  name="first_name"
                  label="First name"
                  placeholder="Enter your first name"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormTextField
                  isRequired
                  name="last_name"
                  label="Last name"
                  placeholder="Enter your last name"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormTextField
                  isRequired
                  name="email"
                  label="Email"
                  type="email"
                  placeholder="Enter your email address"
                  helperText="Your account activation link is sent to this address."
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormTextField
                  isRequired
                  name="phone_number"
                  label="Phone number"
                  type="tel"
                  placeholder="Digits only, e.g. 9123456789"
                  helperText={`${PHONE_MIN_DIGITS}–${PHONE_MAX_DIGITS} digits, no spaces or symbols.`}
                  inputProps={{ inputMode: "numeric" }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="country_id"
                  render={({ field, fieldState: { error } }) => (
                    <Autocomplete
                      size="small"
                      options={countries}
                      getOptionLabel={(option) => option.title}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      value={
                        countries.find(
                          (country) => String(country.id) === field.value,
                        ) ?? null
                      }
                      onChange={(_event, option) => {
                        selectedCountryRef.current = option;
                        field.onChange(option ? String(option.id) : "");
                        formRef.current?.trigger("phone_number");
                      }}
                      onBlur={field.onBlur}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          margin="normal"
                          label={requiredLabel("Country", true)}
                          error={!!error}
                          helperText={error?.message}
                          sx={fieldSx}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormTextField
                  isRequired
                  name="address"
                  label="Address"
                  placeholder="Street, city, postal code"
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            step={2}
            title="Affiliation"
            description="Give your affiliation in separate parts. They are published exactly as entered, so please avoid abbreviations."
          >
            <Grid container spacing={{ xs: 0, md: 2 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormTextField
                  name="affiliation.department"
                  label="Department"
                  placeholder="e.g. Department of Biology"
                  inputProps={{ maxLength: AFFILIATION_PART_MAX_LENGTH }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormTextField
                  isRequired
                  name="affiliation.institution"
                  label="Institution"
                  placeholder="e.g. University of Example"
                  inputProps={{ maxLength: AFFILIATION_PART_MAX_LENGTH }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormTextField
                  name="affiliation.street_address"
                  label="Street address"
                  placeholder="e.g. 12 University Avenue"
                  inputProps={{ maxLength: AFFILIATION_PART_MAX_LENGTH }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormTextField
                  isRequired
                  name="affiliation.city"
                  label="City"
                  placeholder="e.g. Shanghai"
                  inputProps={{ maxLength: AFFILIATION_PART_MAX_LENGTH }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormTextField
                  name="affiliation.state_or_province"
                  label="State or province"
                  placeholder="Optional"
                  inputProps={{ maxLength: AFFILIATION_PART_MAX_LENGTH }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormTextField
                  name="affiliation.postal_code"
                  label="Postal code"
                  placeholder="Optional"
                  inputProps={{ maxLength: AFFILIATION_PART_MAX_LENGTH }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="affiliation.country_id"
                  render={({ field, fieldState: { error } }) => (
                    <Autocomplete
                      size="small"
                      options={countries}
                      getOptionLabel={(option) => option.title}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      value={
                        countries.find(
                          (country) => String(country.id) === field.value,
                        ) ?? null
                      }
                      onChange={(_event, option) =>
                        field.onChange(option ? String(option.id) : "")
                      }
                      onBlur={field.onBlur}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          margin="normal"
                          label={requiredLabel("Country", true)}
                          error={!!error}
                          helperText={error?.message}
                          sx={fieldSx}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            step={3}
            title="Your request"
            description={
              lockedJournal
                ? `Choose the role you are applying for at ${lockedJournal.title}.`
                : "Choose the role you are applying for and the journal that should review your application."
            }
          >
            <Grid container spacing={{ xs: 0, md: 2 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="request_type"
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      size="small"
                      margin="normal"
                      label={requiredLabel("Request type", true)}
                      error={!!error}
                      helperText={error?.message}
                      value={field.value ?? ""}
                      sx={fieldSx}
                    >
                      {REQUEST_TYPES.map((requestType) => (
                        <MenuItem
                          key={requestType.value}
                          value={requestType.value}
                        >
                          {requestType.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              {!lockedJournal && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="journal_id"
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        size="small"
                        options={journals}
                        getOptionLabel={(option) => option.title}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        value={
                          journals.find(
                            (journal) => String(journal.id) === field.value,
                          ) ?? null
                        }
                        onChange={(_event, option) => {
                          field.onChange(option ? String(option.id) : "");
                          setSelectedJournalId(option?.id ?? null);
                        }}
                        onBlur={field.onBlur}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            margin="normal"
                            label={requiredLabel("Journal", true)}
                            error={!!error}
                            helperText={error?.message}
                            sx={fieldSx}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="classifications"
                  render={({ field, fieldState: { error } }) => (
                    <Autocomplete
                      multiple
                      size="small"
                      filterSelectedOptions
                      options={classificationSearch.options}
                      loading={classificationSearch.isLoading}
                      filterOptions={(options) => options}
                      getOptionLabel={(option) => option.title}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      value={field.value ?? []}
                      onChange={(_event, options) => field.onChange(options)}
                      onBlur={field.onBlur}
                      onInputChange={(_event, value) =>
                        classificationSearch.setQuery(value)
                      }
                      noOptionsText="No matching classification"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          margin="normal"
                          label={requiredLabel("Classifications", true)}
                          placeholder="Search your fields of expertise"
                          error={!!error}
                          helperText={
                            error?.message ??
                            "Start typing to search the classification list."
                          }
                          sx={fieldSx}
                          slotProps={{
                            ...params.slotProps,
                            input: {
                              ...params.slotProps.input,
                              endAdornment: (
                                <>
                                  {classificationSearch.isLoading && (
                                    <CircularProgress
                                      color="inherit"
                                      size={16}
                                    />
                                  )}
                                  {params.slotProps.input.endAdornment}
                                </>
                              ),
                            },
                          }}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="keywords"
                  render={({ field, fieldState: { error } }) => (
                    <Autocomplete
                      multiple
                      freeSolo
                      size="small"
                      filterSelectedOptions
                      options={keywordSearch.options}
                      loading={keywordSearch.isLoading}
                      filterOptions={(options) => options}
                      value={field.value ?? []}
                      onChange={(_event, options) =>
                        field.onChange(
                          options
                            .map((option) => String(option).trim())
                            .filter(Boolean),
                        )
                      }
                      onBlur={field.onBlur}
                      onInputChange={(_event, value) =>
                        keywordSearch.setQuery(value)
                      }
                      noOptionsText="Type at least two characters, then press Enter to add"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          margin="normal"
                          label={requiredLabel("Keywords", true)}
                          placeholder="Search or type your own"
                          error={!!error}
                          helperText={
                            error?.message ??
                            "Press Enter to add a keyword that is not listed."
                          }
                          sx={fieldSx}
                          slotProps={{
                            ...params.slotProps,
                            input: {
                              ...params.slotProps.input,
                              endAdornment: (
                                <>
                                  {keywordSearch.isLoading && (
                                    <CircularProgress
                                      color="inherit"
                                      size={16}
                                    />
                                  )}
                                  {params.slotProps.input.endAdornment}
                                </>
                              ),
                            },
                          }}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            step={4}
            title="Documents"
            description={`A profile photo and your CV, each up to ${MAX_FILE_SIZE_MB} MB.`}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FileField
                  label="Profile image"
                  hint={`${PHOTO_EXTENSIONS.join(", ")} — up to ${MAX_FILE_SIZE_MB} MB`}
                  accept=".jpg,.jpeg,.png"
                  file={photo}
                  error={fileErrors.photo}
                  inputRef={photoInputRef}
                  onSelect={handleFileSelect(
                    setPhoto,
                    PHOTO_EXTENSIONS,
                    "Profile image",
                    "photo",
                  )}
                  onClear={() => clearFile(setPhoto, photoInputRef, "photo")}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FileField
                  label="CV"
                  hint={`${RESUME_EXTENSIONS.join(", ")} — up to ${MAX_FILE_SIZE_MB} MB`}
                  accept=".pdf,.docx,.zip"
                  file={resume}
                  error={fileErrors.resume}
                  inputRef={resumeInputRef}
                  onSelect={handleFileSelect(
                    setResume,
                    RESUME_EXTENSIONS,
                    "CV",
                    "resume",
                  )}
                  onClear={() => clearFile(setResume, resumeInputRef, "resume")}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            step={5}
            title="Confirm and submit"
            description="Enter the security code to prove you are not a robot."
          >
            <Grid container spacing={{ xs: 0, md: 2 }}>
              {/* Wider than a half-width field: the captcha image and its
                  refresh button take ~200px of the input's own width. */}
              <Grid size={{ xs: 12, md: 8 }}>
                <FormTextField
                  isRequired
                  name="captcha_phrase"
                  label="Security code"
                  placeholder="Enter the code shown"
                  inputProps={{ autoComplete: "off" }}
                  sx={captchaFieldSx}
                  endAdornment={
                    <InputAdornment position="end">
                      <Box sx={captchaFrameSx}>
                        {captcha.captcha && (
                          <img
                            src={captcha.captcha}
                            alt="Security code"
                            style={captchaImageStyle}
                          />
                        )}
                        <Button
                          variant="contained"
                          color="primary"
                          type="button"
                          onClick={refreshCaptcha}
                          sx={captchaButtonSx}
                        >
                          New code
                        </Button>
                      </Box>
                    </InputAdornment>
                  }
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                gap: 2,
                mt: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={isSubmitting || !captcha.key}
                onClick={validateFiles}
                sx={actionSx("solid")}
              >
                {isSubmitting ? "Submitting..." : "Submit request"}
              </Button>
              <Typography
                component="p"
                sx={{
                  m: 0,
                  fontFamily: FONT_UI,
                  fontSize: "0.8125rem",
                  color: "custom.inkMuted",
                }}
              >
                Your submission is reviewed by the Editor-in-Chief.
              </Typography>
            </Box>

            {message && (
              <Alert severity={message.severity} sx={{ mt: 2 }}>
                <Typography variant="body2">{message.message}</Typography>
              </Alert>
            )}
          </FormSection>
        </Stack>
      </SchemaWrapper>
    </Box>
  );
}
